import React, { useRef, useState } from "react";
import * as Icons from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";

const TONE = {
  Rejected: "bg-rose-50 text-rose-700 ring-rose-200", "Returned to ADCO": "bg-orange-50 text-orange-700 ring-orange-200",
  Routed: "bg-emerald-50 text-emerald-700 ring-emerald-200", Posted: "bg-emerald-50 text-emerald-700 ring-emerald-200",
  "Link Sent": "bg-emerald-50 text-emerald-700 ring-emerald-200", Obsoleted: "bg-slate-200 text-slate-700 ring-slate-300",
  Obsolete: "bg-slate-200 text-slate-700 ring-slate-300", "Link Expired": "bg-slate-100 text-slate-500 ring-slate-200",
};
export const Status = ({ s, tid }) => <span className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold ring-1 whitespace-nowrap ${TONE[s] || "bg-amber-50 text-amber-700 ring-amber-200"}`} data-testid={tid}>{s}</span>;

export const STAMP_TONE = { Mastercopy: "text-blue-700 border-blue-300", Controlled: "text-emerald-700 border-emerald-300", Uncontrolled: "text-amber-700 border-amber-300", Obsolete: "text-slate-500 border-slate-300" };
export const Stamp = ({ s }) => s ? <span className={`inline-block rotate-[-4deg] border-2 rounded px-1.5 text-[9px] font-black uppercase tracking-widest ${STAMP_TONE[s] || ""}`}>{s}</span> : null;

export const openFile = async (fid, token = "") => {
  const w = window.open("", "_blank");
  try {
    const res = await api.get(`/dms/files/${fid}${token ? `?token=${token}` : ""}`, { responseType: "blob" });
    w.location.href = URL.createObjectURL(res.data);
  } catch (e) { w.close(); toast.error("You don't have access to this file"); }
};

export const FilePick = ({ value, onChange, tid, label = "Attach document", onBusy }) => {
  const ref = useRef();
  const [busy, setBusyState] = useState(false);
  const setBusy = (v) => { setBusyState(v); onBusy && onBusy(v); };
  const pick = async (f) => {
    if (!f) return;
    setBusy(true);
    const fd = new FormData(); fd.append("file", f);
    try { const { data } = await api.post("/dms/files", fd); onChange(data); toast.success("File attached"); } catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); }
    setBusy(false);
  };
  return (
    <div className="flex items-center gap-2">
      <input ref={ref} type="file" className="hidden" onChange={(e) => pick(e.target.files[0])} data-testid={tid} />
      <Button type="button" variant="outline" size="sm" onClick={() => ref.current.click()} disabled={busy}><Icons.Paperclip className="h-3.5 w-3.5 mr-1.5" />{busy ? "Uploading…" : label}</Button>
      {value && <span className="text-xs text-emerald-700 truncate max-w-[220px]">{value.filename}</span>}
    </div>
  );
};

export const History = ({ items }) => (
  <ol className="relative border-l border-slate-200 ml-2 space-y-3" data-testid="dms-history">
    {(items || []).map((h, i) => (
      <li key={i} className="ml-4"><span className="absolute -left-1.5 mt-1 h-3 w-3 rounded-full bg-white border-2 border-blue-500" />
        <div className="text-xs font-medium text-slate-800">{h.action}</div>
        <div className="text-[11px] text-slate-500">{h.by} · {new Date(h.at).toLocaleString()}</div>
        {h.note && <div className="text-[11px] text-slate-600 mt-0.5">“{h.note}”</div>}
      </li>
    ))}
  </ol>
);

export const Steps = ({ steps, current, rejected }) => (
  <div className="flex flex-wrap items-center gap-1" data-testid="dms-steps">
    {steps.map((s, i) => {
      const done = i < current, cur = i === current;
      return (
        <React.Fragment key={s}>
          {i > 0 && <span className={`h-px w-4 ${done ? "bg-emerald-400" : "bg-slate-200"}`} />}
          <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${done ? "bg-emerald-50 text-emerald-700" : cur ? (rejected ? "bg-rose-50 text-rose-700" : "bg-amber-50 text-amber-700 ring-1 ring-amber-300") : "bg-slate-50 text-slate-400"}`}>{done && <Icons.Check className="inline h-3 w-3 mr-0.5" />}{s}</span>
        </React.Fragment>
      );
    })}
  </div>
);

export const csv = (name, head, rows) => {
  const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const blob = new Blob(["\ufeff" + [head, ...rows].map((r) => r.map(esc).join(",")).join("\r\n")], { type: "text/csv" });
  const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = name; a.click();
};

export const selW = "border border-slate-200 rounded-lg h-10 px-3 text-sm bg-white";
export const sel = `w-full ${selW}`;
